import React, { useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { Alert, Button, Popover, Select, Switch } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { COLORS } from "../../../consts";
import { useUser } from "../../../context/UserContext";
import { hexToRgba } from "./utils";
import {
  positiveTrendFlagOn,
  reorderRecordsByFieldKeepingDate,
} from "../../../utils/positiveTrendCorrection";

const toFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const buildGrowthRateSeries = (labels, values, format) => {
  let previousValue = null;
  let previousDate = null;

  return labels.map((label, index) => {
    const currentValue = toFiniteNumber(values[index]);
    const currentDate = dayjs(label, format);
    if (!currentDate.isValid() || currentValue === null) {
      return null;
    }

    if (previousValue === null || !previousDate?.isValid()) {
      previousValue = currentValue;
      previousDate = currentDate;
      return null;
    }

    const dayDifference = Math.max(currentDate.diff(previousDate, "day"), 1);
    const growthRate = (currentValue - previousValue) / dayDifference;
    previousValue = currentValue;
    previousDate = currentDate;
    return Number.isFinite(growthRate) ? growthRate : null;
  });
};

const buildCumulativeSeries = (values) => {
  const firstValidValue = values.find((value) => value !== null);
  if (firstValidValue === undefined) {
    return values.map(() => null);
  }

  return values.map((value) =>
    value === null ? null : value - firstValidValue,
  );
};

const AverageGraph = ({
  averageData,
  allRowAverageData,
  visibility,
  setVisibility,
  forecastLeafSizeLength,
  isAllRowCombined,
  setIsAllRowCombined,
  siteMetadata,
  tableData,
  blockLabel,
  isFullscreen = false,
}) => {
  const GRAPH_UI_DATE_FORMAT = "DD MMM YY";
  const fallbackIdealLeafSize = Number(
    siteMetadata?.IDEAL_HARVEST_LEAF_SIZE_FOR_FORECAST ?? 7,
  );
  const configuredMinIdealLeafSize = Number(
    siteMetadata?.MIN_IDEAL_HARVEST_LEAF_SIZE,
  );
  const configuredMaxIdealLeafSize = Number(
    siteMetadata?.MAX_IDEAL_HARVEST_LEAF_SIZE,
  );
  const minIdealLeafSize =
    Number.isFinite(configuredMinIdealLeafSize) &&
    configuredMinIdealLeafSize > 0
      ? configuredMinIdealLeafSize
      : null;
  const maxIdealLeafSize =
    Number.isFinite(configuredMaxIdealLeafSize) &&
    configuredMaxIdealLeafSize > 0
      ? configuredMaxIdealLeafSize
      : null;
  const targetLeafSize =
    maxIdealLeafSize ??
    (Number.isFinite(fallbackIdealLeafSize) && fallbackIdealLeafSize > 0
      ? fallbackIdealLeafSize
      : 7);
  const minIdealReadinessPercent =
    minIdealLeafSize !== null && targetLeafSize > 0
      ? (minIdealLeafSize / targetLeafSize) * 100
      : null;
  const { userData, isAdmin } = useUser();
  const isSuperUser = Boolean(userData?.is_superuser);
  // OF-129: gate the positive-trend correction — non-admins on an opted-in
  // site+crop. `siteMetadata` here is already the crop-scoped metadata object.
  const applyTrendCorrection = useMemo(() => {
    const admin = typeof isAdmin === "function" ? isAdmin() : false;
    return !admin && positiveTrendFlagOn(siteMetadata);
  }, [isAdmin, siteMetadata]);

  const [graphType, setGraphType] = useState("line");
  const [advancedVisibility, setAdvancedVisibility] = useState({
    avgGap: true,
    percentileGap: true,
    targetBaseline: true,
    avgGrowthRate: true,
    percentileGrowthRate: true,
    growthBaseline: true,
    stdDev: true,
    coefficientVariation: true,
    avgReadiness: true,
    percentileReadiness: true,
    readinessBaseline: true,
    minReadinessBaseline: true,
    avgCumulativeGrowth: true,
    percentileCumulativeGrowth: true,
    cumulativeBaseline: true,
    upperTailLift: true,
    upperTailLiftPercent: true,
    upperTailBaseline: true,
  });

  const yMinMeta = Number(siteMetadata?.AVERAGE_TREND_GRAPH_Y_AXIS_MIN_VALUE);
  const yMaxMeta = Number(siteMetadata?.AVERAGE_TREND_GRAPH_Y_AXIS_MAX_VALUE);
  const yAxisMin = Number.isFinite(yMinMeta) ? yMinMeta : undefined;
  const yAxisMax = Number.isFinite(yMaxMeta) ? yMaxMeta : undefined;

  const graphTypeOptions = [
    { label: "Line", value: "line" },
    { label: "Target Gap", value: "targetGap" },
    { label: "Growth Rate", value: "growthRate" },
    { label: "Stability", value: "stability" },
    { label: "Readiness %", value: "readiness" },
    { label: "Cumulative Growth", value: "cumulativeGrowth" },
    { label: "Upper-Tail Lift", value: "upperTailLift" },
  ];

  const graphInfoByType = {
    line: {
      short:
        "Line Trend: Track current and forecast leaf-size trajectories for average and upper-percentile growth.",
      relevance:
        "Best for checking whether current progression aligns with expected harvest-size movement.",
      details: [
        "Average line uses averageLength per date.",
        "Leaf Size (80-95 percentile) trend uses averagePercentile80_95 per date.",
        "Leaf Size Range band is averageLength ± stdDev.",
        "Forecast lines use cumulative forecast deltas anchored from the latest observed point.",
      ],
    },
    targetGap: {
      short:
        "Target Gap Trend: Shows how far observed/forecasted leaf size sits from the max ideal harvest target.",
      relevance:
        "Best for quickly seeing if blocks are below or above the max ideal leaf size and by how much.",
      details: [
        "Gap is calculated as: measured leaf size - max ideal harvest leaf size.",
        "Zero baseline means exactly at the configured max ideal leaf size.",
        "Positive values indicate above-target size; negative values indicate below-target size.",
      ],
    },
    growthRate: {
      short:
        "Growth Rate Trend: Displays day-over-day growth speed rather than absolute size values.",
      relevance:
        "Best for spotting acceleration/slowdown and comparing growth momentum across dates.",
      details: [
        "Growth rate is calculated between consecutive available dates.",
        "Formula: (current leaf size - previous leaf size) / day difference.",
        "Rates are shown in cm/day for both average and 80-95 percentile trend.",
      ],
    },
    stability: {
      short:
        "Stability Trend: Quantifies variability and consistency of leaf sizes over time.",
      relevance:
        "Best for assessing if crop development is becoming more uniform approaching harvest.",
      details: [
        "Std Dev is taken directly from average data for each date.",
        "Coefficient of variation (CV%) is calculated as: (stdDev / averageLength) * 100.",
        "Lower CV% generally indicates tighter, more uniform size spread.",
      ],
    },
    readiness: {
      short:
        "Readiness Trend: Shows each date's leaf size as a percentage of max ideal harvest size.",
      relevance:
        "Best for checking readiness progression relative to your configured min/max ideal range.",
      details: [
        "Readiness is calculated as: (leaf size / max ideal harvest leaf size) * 100.",
        "100% baseline means exactly at the configured max ideal harvest leaf size.",
        "Min ideal baseline is shown at: (min ideal / max ideal) * 100.",
        "Values above 100% indicate size above max ideal; values below the min baseline are below ideal range.",
      ],
    },
    cumulativeGrowth: {
      short:
        "Cumulative Growth: Displays net growth since the first available observation date.",
      relevance:
        "Best for comparing total growth gained over time, independent of starting size.",
      details: [
        "Cumulative growth is calculated as: current size - first available size.",
        "A zero baseline marks the first available observed point.",
        "Both average and 80-95 percentile trend are shown for side-by-side comparison.",
      ],
    },
    upperTailLift: {
      short:
        "Upper-Tail Lift: Highlights how much larger the upper-percentile leaves are than the average.",
      relevance:
        "Best for understanding distribution skew and whether top-end growth is outpacing average growth.",
      details: [
        "Upper-tail lift (cm) is calculated as: (80-95 percentile size - average size).",
        "Upper-tail lift (%) is calculated as: (upper-tail lift / average size) * 100.",
        "Higher lift can indicate stronger right-tail behavior in size distribution.",
      ],
    },
  };

  const activeGraphInfo = graphInfoByType[graphType] || graphInfoByType.line;

  const harvestDate = useMemo(() => {
    const rawHarvestDate = tableData?.[0]?.harvestDate;
    return rawHarvestDate ? dayjs(rawHarvestDate) : null;
  }, [tableData]);

  const rawDataToUse = useMemo(
    () => (isAllRowCombined ? allRowAverageData : averageData) || [],
    [isAllRowCombined, allRowAverageData, averageData],
  );

  const dataToUse = useMemo(() => {
    // Harvest-day datapoint handling. Only the CORRECTED (customer) view excludes
    // it (isBefore), to match the forecast page window [planting, harvestDate)
    // (endInclusive=false). Admins and non-opted-in sites keep the original
    // isSameOrBefore so they still see every datapoint (OF-129: admins see raw).
    let clipped = rawDataToUse;
    if (harvestDate) {
      clipped = rawDataToUse.filter((point) => {
        const pointDate = dayjs(point.date, GRAPH_UI_DATE_FORMAT);
        return applyTrendCorrection
          ? pointDate.isBefore(harvestDate, "day")
          : pointDate.isSameOrBefore(harvestDate, "day");
      });
    }
    // OF-129: when the 80-95 percentile trend (the metric the forecast/growth
    // rate is derived from) reads as declining, reorder the WHOLE per-date record
    // ascending by that percentile so the trend never shows shrinkage. Moving the
    // record as a bundle keyed on one metric keeps 80-95 >= average on every slot
    // (no line crossing) and keeps stdDev paired with its own mean. Dates stay
    // fixed; display-only; runs after the harvest clip.
    if (applyTrendCorrection) {
      clipped = reorderRecordsByFieldKeepingDate(
        clipped,
        "averagePercentile80_95",
      );
    }
    return clipped;
  }, [harvestDate, rawDataToUse, applyTrendCorrection]);

  const forecastDataToUse = useMemo(() => {
    if (harvestDate) {
      return [];
    }
    return forecastLeafSizeLength || [];
  }, [harvestDate, forecastLeafSizeLength]);

  const historicalPointByDate = useMemo(() => {
    const pointMap = new Map();
    dataToUse.forEach((point) => {
      pointMap.set(point.date, point);
    });
    return pointMap;
  }, [dataToUse]);

  const [lastHistoricDate, lastHistoricValue] = useMemo(() => {
    if (!dataToUse?.length) return [null, 0];

    const sorted = [...dataToUse].sort((a, b) => {
      const dateA = dayjs(a.date, GRAPH_UI_DATE_FORMAT);
      const dateB = dayjs(b.date, GRAPH_UI_DATE_FORMAT);
      return dateA.diff(dateB);
    });

    const lastEntry = sorted[sorted.length - 1];
    return [lastEntry.date, Number(lastEntry.averageLength) || 0];
  }, [dataToUse]);

  const [lastPercentileHistoricDate, lastPercentileHistoricValue] =
    useMemo(() => {
      if (!dataToUse?.length) return [null, 0];

      const filtered = dataToUse.filter(
        (point) => point.averagePercentile80_95 != null,
      );
      if (!filtered?.length) return [null, 0];

      const sorted = filtered.sort((a, b) => {
        const dateA = dayjs(a.date, GRAPH_UI_DATE_FORMAT);
        const dateB = dayjs(b.date, GRAPH_UI_DATE_FORMAT);
        return dateA.diff(dateB);
      });
      const lastEntry = sorted[sorted.length - 1];
      return [lastEntry.date, Number(lastEntry.averagePercentile80_95) || 0];
    }, [dataToUse]);

  const bridgedForecast = useMemo(() => {
    if (!forecastDataToUse?.length || !lastHistoricDate) {
      return forecastDataToUse;
    }
    const bridging = {
      date: lastHistoricDate,
      averageLength: 0,
      averagePercentile80_95: 0,
    };
    return [bridging, ...forecastDataToUse];
  }, [forecastDataToUse, lastHistoricDate]);

  const bridgedPercentileForecast = useMemo(() => {
    if (!forecastDataToUse?.length || !lastPercentileHistoricDate) {
      return forecastDataToUse;
    }
    const bridging = {
      date: lastPercentileHistoricDate,
      averageLength: 0,
      averagePercentile80_95: 0,
    };
    return [bridging, ...forecastDataToUse];
  }, [forecastDataToUse, lastPercentileHistoricDate]);

  const historicalLabels = useMemo(
    () => dataToUse.map((point) => point.date),
    [dataToUse],
  );

  const forecastLabels = useMemo(() => {
    if (visibility["Forecast Leaf Size"] === false) return [];
    return (bridgedForecast || []).map((point) => point.date);
  }, [bridgedForecast, visibility]);

  const isForecastAboveIdeal = useMemo(() => {
    if (!forecastDataToUse?.length) return false;
    return bridgedForecast.some((point) => {
      const value =
        lastPercentileHistoricValue +
        (Number(point.averagePercentile80_95) || 0);
      return value > targetLeafSize;
    });
  }, [
    bridgedForecast,
    lastPercentileHistoricValue,
    targetLeafSize,
    forecastDataToUse,
  ]);

  const finalLabels = useMemo(() => {
    const combined = isForecastAboveIdeal
      ? [...historicalLabels, ...forecastLabels]
      : [...historicalLabels];
    const unique = Array.from(new Set(combined));
    unique.sort((a, b) => {
      const dateA = dayjs(a, GRAPH_UI_DATE_FORMAT);
      const dateB = dayjs(b, GRAPH_UI_DATE_FORMAT);
      return dateA.diff(dateB);
    });
    return unique;
  }, [historicalLabels, forecastLabels, isForecastAboveIdeal]);

  const forecastDataset = useMemo(() => {
    return finalLabels.map((label) => {
      const found = bridgedForecast.find((point) => point.date === label);
      if (!found) return null;
      return lastHistoricValue + (Number(found.averageLength) || 0);
    });
  }, [finalLabels, bridgedForecast, lastHistoricValue]);

  const forecastPercentileDataset = useMemo(() => {
    return finalLabels.map((label) => {
      const found = bridgedPercentileForecast.find((point) => point.date === label);
      if (!found) return null;
      return (
        lastPercentileHistoricValue +
        (Number(found.averagePercentile80_95) || 0)
      );
    });
  }, [finalLabels, bridgedPercentileForecast, lastPercentileHistoricValue]);

  const mergedAverageSeries = useMemo(
    () =>
      finalLabels.map((label, index) => {
        const historical = toFiniteNumber(
          historicalPointByDate.get(label)?.averageLength,
        );
        if (historical !== null) return historical;
        if (!isForecastAboveIdeal) return null;
        return toFiniteNumber(forecastDataset[index]);
      }),
    [finalLabels, historicalPointByDate, isForecastAboveIdeal, forecastDataset],
  );

  const mergedPercentileSeries = useMemo(
    () =>
      finalLabels.map((label, index) => {
        const historical = toFiniteNumber(
          historicalPointByDate.get(label)?.averagePercentile80_95,
        );
        if (historical !== null) return historical;
        if (!isForecastAboveIdeal) return null;
        return toFiniteNumber(forecastPercentileDataset[index]);
      }),
    [
      finalLabels,
      historicalPointByDate,
      isForecastAboveIdeal,
      forecastPercentileDataset,
    ],
  );

  const stdDevSeries = useMemo(
    () =>
      finalLabels.map((label) =>
        toFiniteNumber(historicalPointByDate.get(label)?.stdDev),
      ),
    [finalLabels, historicalPointByDate],
  );

  const coefficientVariationSeries = useMemo(
    () =>
      finalLabels.map((label) => {
        const point = historicalPointByDate.get(label);
        const averageLength = toFiniteNumber(point?.averageLength);
        const stdDev = toFiniteNumber(point?.stdDev);
        if (averageLength === null || averageLength === 0 || stdDev === null) {
          return null;
        }
        return (stdDev / averageLength) * 100;
      }),
    [finalLabels, historicalPointByDate],
  );

  const averageGapSeries = useMemo(
    () =>
      mergedAverageSeries.map((value) =>
        value === null ? null : value - targetLeafSize,
      ),
    [mergedAverageSeries, targetLeafSize],
  );

  const percentileGapSeries = useMemo(
    () =>
      mergedPercentileSeries.map((value) =>
        value === null ? null : value - targetLeafSize,
      ),
    [mergedPercentileSeries, targetLeafSize],
  );

  const averageGrowthRateSeries = useMemo(
    () =>
      buildGrowthRateSeries(
        finalLabels,
        mergedAverageSeries,
        GRAPH_UI_DATE_FORMAT,
      ),
    [finalLabels, mergedAverageSeries],
  );

  const percentileGrowthRateSeries = useMemo(
    () =>
      buildGrowthRateSeries(
        finalLabels,
        mergedPercentileSeries,
        GRAPH_UI_DATE_FORMAT,
      ),
    [finalLabels, mergedPercentileSeries],
  );

  const averageReadinessSeries = useMemo(
    () =>
      mergedAverageSeries.map((value) => {
        if (value === null || !Number.isFinite(targetLeafSize) || targetLeafSize === 0) {
          return null;
        }
        return (value / targetLeafSize) * 100;
      }),
    [mergedAverageSeries, targetLeafSize],
  );

  const percentileReadinessSeries = useMemo(
    () =>
      mergedPercentileSeries.map((value) => {
        if (value === null || !Number.isFinite(targetLeafSize) || targetLeafSize === 0) {
          return null;
        }
        return (value / targetLeafSize) * 100;
      }),
    [mergedPercentileSeries, targetLeafSize],
  );

  const averageCumulativeGrowthSeries = useMemo(
    () => buildCumulativeSeries(mergedAverageSeries),
    [mergedAverageSeries],
  );

  const percentileCumulativeGrowthSeries = useMemo(
    () => buildCumulativeSeries(mergedPercentileSeries),
    [mergedPercentileSeries],
  );

  const upperTailLiftSeries = useMemo(
    () =>
      mergedAverageSeries.map((averageValue, index) => {
        const percentileValue = mergedPercentileSeries[index];
        if (averageValue === null || percentileValue === null) {
          return null;
        }
        return percentileValue - averageValue;
      }),
    [mergedAverageSeries, mergedPercentileSeries],
  );

  const upperTailLiftPercentSeries = useMemo(
    () =>
      mergedAverageSeries.map((averageValue, index) => {
        const liftValue = upperTailLiftSeries[index];
        if (averageValue === null || averageValue === 0 || liftValue === null) {
          return null;
        }
        return (liftValue / averageValue) * 100;
      }),
    [mergedAverageSeries, upperTailLiftSeries],
  );

  const averageChartData = {
    labels: finalLabels,
    datasets: [
      {
        label: "Average Leaf Size",
        data: dataToUse.map((point) => point.averageLength),
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        fill: false,
        tension: 0.3,
        hidden: !visibility["Average Spinach Leaf Size (cm)"],
        spanGaps: true,
      },
      {
        label: "Upper Leaf Size Range",
        data: dataToUse.map((point) => point.averageLength + point.stdDev),
        borderColor: "#a9cff5",
        backgroundColor: hexToRgba("#a9cff5", 0.1),
        fill: "-1",
        tension: 0.3,
        hidden: !visibility["Standard Deviation"],
        spanGaps: true,
      },
      {
        label: "Lower Leaf Size Range",
        data: dataToUse.map((point) => point.averageLength - point.stdDev),
        borderColor: "#a9cff5",
        backgroundColor: hexToRgba("#a9cff5", 0.1),
        fill: "-1",
        tension: 0.3,
        hidden: !visibility["Standard Deviation"],
        spanGaps: true,
      },
      {
        label: "Leaf Size (80-95 percentile) Trend",
        data: dataToUse.map((point) => point.averagePercentile80_95),
        borderColor: COLORS.successGreen,
        backgroundColor: hexToRgba(COLORS.successGreen, 0.1),
        fill: "-1",
        tension: 0.3,
        hidden: !visibility["Leaf Size (80-95 percentile) Trend"],
        spanGaps: true,
      },
      {
        label: "Forecast Leaf Size",
        data: forecastDataset,
        borderColor: COLORS.primaryOrange,
        backgroundColor: COLORS.primaryOrange,
        fill: false,
        tension: 0.3,
        hidden: !visibility["Forecast Leaf Size"] || !isForecastAboveIdeal,
        spanGaps: true,
        borderDash: [5, 5],
      },
      {
        label: "Forecast Leaf Size (80-95 percentile)",
        data: forecastPercentileDataset,
        borderColor: COLORS.primaryOrange,
        backgroundColor: COLORS.primaryOrange,
        fill: false,
        tension: 0.3,
        hidden: !visibility["Forecast Leaf Size"] || !isForecastAboveIdeal,
        spanGaps: true,
        borderDash: [5, 5],
      },
    ],
  };

  const targetGapChartData = {
    labels: finalLabels,
    datasets: [
      {
        label: "Average Gap vs Max Ideal",
        data: averageGapSeries,
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        tension: 0.3,
        fill: false,
        spanGaps: true,
        hidden: !advancedVisibility.avgGap,
      },
      {
        label: "80-95 Percentile Gap vs Max Ideal",
        data: percentileGapSeries,
        borderColor: COLORS.successGreen,
        backgroundColor: COLORS.successGreen,
        tension: 0.3,
        fill: false,
        spanGaps: true,
        hidden: !advancedVisibility.percentileGap,
      },
      {
        label: "Ideal Target Baseline",
        data: finalLabels.map(() => 0),
        borderColor: "#8c8c8c",
        backgroundColor: "#8c8c8c",
        tension: 0,
        fill: false,
        spanGaps: true,
        borderDash: [6, 4],
        pointRadius: 0,
        hidden: !advancedVisibility.targetBaseline,
      },
    ],
  };

  const growthRateChartData = {
    labels: finalLabels,
    datasets: [
      {
        label: "Average Growth Rate",
        data: averageGrowthRateSeries,
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        tension: 0.3,
        fill: false,
        spanGaps: true,
        hidden: !advancedVisibility.avgGrowthRate,
      },
      {
        label: "80-95 Percentile Growth Rate",
        data: percentileGrowthRateSeries,
        borderColor: COLORS.successGreen,
        backgroundColor: COLORS.successGreen,
        tension: 0.3,
        fill: false,
        spanGaps: true,
        hidden: !advancedVisibility.percentileGrowthRate,
      },
      {
        label: "No-Change Baseline",
        data: finalLabels.map(() => 0),
        borderColor: "#8c8c8c",
        backgroundColor: "#8c8c8c",
        tension: 0,
        fill: false,
        spanGaps: true,
        borderDash: [6, 4],
        pointRadius: 0,
        hidden: !advancedVisibility.growthBaseline,
      },
    ],
  };

  const stabilityChartData = {
    labels: finalLabels,
    datasets: [
      {
        label: "Leaf Size Variability (Std Dev)",
        data: stdDevSeries,
        borderColor: COLORS.primaryOrange,
        backgroundColor: COLORS.primaryOrange,
        tension: 0.3,
        fill: false,
        spanGaps: true,
        hidden: !advancedVisibility.stdDev,
        yAxisID: "y",
      },
      {
        label: "Consistency Score (CV %)",
        data: coefficientVariationSeries,
        borderColor: "#722ed1",
        backgroundColor: "#722ed1",
        tension: 0.3,
        fill: false,
        spanGaps: true,
        hidden: !advancedVisibility.coefficientVariation,
        yAxisID: "y1",
      },
    ],
  };

  const readinessChartData = {
    labels: finalLabels,
    datasets: [
      {
        label: "Average Readiness (%)",
        data: averageReadinessSeries,
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        tension: 0.3,
        fill: false,
        spanGaps: true,
        hidden: !advancedVisibility.avgReadiness,
      },
      {
        label: "80-95 Percentile Readiness (%)",
        data: percentileReadinessSeries,
        borderColor: COLORS.successGreen,
        backgroundColor: COLORS.successGreen,
        tension: 0.3,
        fill: false,
        spanGaps: true,
        hidden: !advancedVisibility.percentileReadiness,
      },
      {
        label: "Ideal Readiness Baseline",
        data: finalLabels.map(() => 100),
        borderColor: "#8c8c8c",
        backgroundColor: "#8c8c8c",
        tension: 0,
        fill: false,
        spanGaps: true,
        borderDash: [6, 4],
        pointRadius: 0,
        hidden: !advancedVisibility.readinessBaseline,
      },
      {
        label: `Min Ideal Baseline (${minIdealLeafSize?.toFixed(1) ?? "N/A"} cm)`,
        data: finalLabels.map(() => minIdealReadinessPercent),
        borderColor: "#bfbfbf",
        backgroundColor: "#bfbfbf",
        tension: 0,
        fill: false,
        spanGaps: true,
        borderDash: [4, 4],
        pointRadius: 0,
        hidden:
          !advancedVisibility.minReadinessBaseline ||
          minIdealReadinessPercent === null,
      },
    ],
  };

  const cumulativeGrowthChartData = {
    labels: finalLabels,
    datasets: [
      {
        label: "Average Cumulative Growth",
        data: averageCumulativeGrowthSeries,
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        tension: 0.3,
        fill: false,
        spanGaps: true,
        hidden: !advancedVisibility.avgCumulativeGrowth,
      },
      {
        label: "80-95 Percentile Cumulative Growth",
        data: percentileCumulativeGrowthSeries,
        borderColor: COLORS.successGreen,
        backgroundColor: COLORS.successGreen,
        tension: 0.3,
        fill: false,
        spanGaps: true,
        hidden: !advancedVisibility.percentileCumulativeGrowth,
      },
      {
        label: "Start Baseline",
        data: finalLabels.map(() => 0),
        borderColor: "#8c8c8c",
        backgroundColor: "#8c8c8c",
        tension: 0,
        fill: false,
        spanGaps: true,
        borderDash: [6, 4],
        pointRadius: 0,
        hidden: !advancedVisibility.cumulativeBaseline,
      },
    ],
  };

  const upperTailLiftChartData = {
    labels: finalLabels,
    datasets: [
      {
        label: "Upper-Tail Lift (cm)",
        data: upperTailLiftSeries,
        borderColor: COLORS.primaryOrange,
        backgroundColor: COLORS.primaryOrange,
        tension: 0.3,
        fill: false,
        spanGaps: true,
        hidden: !advancedVisibility.upperTailLift,
        yAxisID: "y",
      },
      {
        label: "Upper-Tail Lift (%)",
        data: upperTailLiftPercentSeries,
        borderColor: "#722ed1",
        backgroundColor: "#722ed1",
        tension: 0.3,
        fill: false,
        spanGaps: true,
        hidden: !advancedVisibility.upperTailLiftPercent,
        yAxisID: "y1",
      },
      {
        label: "No-Lift Baseline",
        data: finalLabels.map(() => 0),
        borderColor: "#8c8c8c",
        backgroundColor: "#8c8c8c",
        tension: 0,
        fill: false,
        spanGaps: true,
        borderDash: [6, 4],
        pointRadius: 0,
        hidden: !advancedVisibility.upperTailBaseline,
        yAxisID: "y",
      },
    ],
  };

  const averageOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            return value === null
              ? `${tooltipItem.dataset.label}: N/A`
              : `${tooltipItem.dataset.label}: ${Number(value).toFixed(1)} cm`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: {
          display: true,
          text: "Spinach Leaf Size (cm)",
          font: { size: 16 },
        },
        grid: { display: false },
        beginAtZero: false,
        ticks: { font: { size: 14 } },
        ...(yAxisMin !== undefined && { min: yAxisMin }),
        ...(yAxisMax !== undefined && { max: yAxisMax }),
      },
    },
  };

  const targetGapOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const gapValue = toFiniteNumber(tooltipItem.raw);
            if (gapValue === null) {
              return `${tooltipItem.dataset.label}: N/A`;
            }
            const absoluteValue = targetLeafSize + gapValue;
            const gapPrefix = gapValue >= 0 ? "+" : "";
            if (tooltipItem.dataset.label === "Ideal Target Baseline") {
              return `${tooltipItem.dataset.label}: ${absoluteValue.toFixed(2)} cm`;
            }
            return `${tooltipItem.dataset.label}: ${absoluteValue.toFixed(
              2,
            )} cm (${gapPrefix}${gapValue.toFixed(2)} cm vs target)`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: {
          display: true,
          text: "Leaf Size (cm)",
          font: { size: 16 },
        },
        grid: { display: false },
        ticks: {
          font: { size: 14 },
          callback: (value) => {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
              return value;
            }
            return (targetLeafSize + numericValue).toFixed(1);
          },
        },
      },
    },
  };

  const growthRateOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            return value === null
              ? `${tooltipItem.dataset.label}: N/A`
              : `${tooltipItem.dataset.label}: ${Number(value).toFixed(
                  3,
                )} cm/day`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: {
          display: true,
          text: "Growth Rate (cm/day)",
          font: { size: 16 },
        },
        grid: { display: false },
        ticks: { font: { size: 14 } },
      },
    },
  };

  const stabilityOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            if (value === null) return `${tooltipItem.dataset.label}: N/A`;
            if (tooltipItem.dataset.yAxisID === "y1") {
              return `${tooltipItem.dataset.label}: ${Number(value).toFixed(2)}%`;
            }
            return `${tooltipItem.dataset.label}: ${Number(value).toFixed(2)} cm`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: {
          display: true,
          text: "Standard Deviation (cm)",
          font: { size: 16 },
        },
        grid: { display: false },
        ticks: { font: { size: 14 } },
      },
      y1: {
        position: "right",
        title: {
          display: true,
          text: "Consistency (CV %)",
          font: { size: 16 },
        },
        grid: { display: false, drawOnChartArea: false },
        ticks: { font: { size: 14 } },
      },
    },
  };

  const readinessOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const readinessValue = toFiniteNumber(tooltipItem.raw);
            if (readinessValue === null) {
              return `${tooltipItem.dataset.label}: N/A`;
            }

            if (tooltipItem.dataset.label === "Ideal Readiness Baseline") {
              return `${tooltipItem.dataset.label}: ${readinessValue.toFixed(
                1,
              )}% (${targetLeafSize.toFixed(2)} cm)`;
            }

            if (
              tooltipItem.dataset.label.startsWith("Min Ideal Baseline") &&
              minIdealLeafSize !== null
            ) {
              return `${tooltipItem.dataset.label}: ${readinessValue.toFixed(
                1,
              )}% (${minIdealLeafSize.toFixed(2)} cm)`;
            }

            const absoluteLeafSize = (readinessValue / 100) * targetLeafSize;
            return `${tooltipItem.dataset.label}: ${readinessValue.toFixed(
              1,
            )}% (${absoluteLeafSize.toFixed(2)} cm)`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: {
          display: true,
          text: "Readiness (% of Max Ideal)",
          font: { size: 16 },
        },
        grid: { display: false },
        ticks: { font: { size: 14 } },
      },
    },
  };

  const cumulativeGrowthOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            return value === null
              ? `${tooltipItem.dataset.label}: N/A`
              : `${tooltipItem.dataset.label}: ${Number(value).toFixed(2)} cm`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: {
          display: true,
          text: "Cumulative Growth (cm)",
          font: { size: 16 },
        },
        grid: { display: false },
        ticks: { font: { size: 14 } },
      },
    },
  };

  const upperTailLiftOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            if (value === null) return `${tooltipItem.dataset.label}: N/A`;
            if (tooltipItem.dataset.yAxisID === "y1") {
              return `${tooltipItem.dataset.label}: ${Number(value).toFixed(2)}%`;
            }
            return `${tooltipItem.dataset.label}: ${Number(value).toFixed(2)} cm`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: {
          display: true,
          text: "Upper-Tail Lift (cm)",
          font: { size: 16 },
        },
        grid: { display: false },
        ticks: { font: { size: 14 } },
      },
      y1: {
        position: "right",
        title: {
          display: true,
          text: "Upper-Tail Lift (%)",
          font: { size: 16 },
        },
        grid: { display: false, drawOnChartArea: false },
        ticks: { font: { size: 14 } },
      },
    },
  };

  const advancedLegendByGraphType = {
    targetGap: [
      {
        key: "avgGap",
        label: "Average Gap vs Max Ideal",
        color: COLORS.primaryBlue,
      },
      {
        key: "percentileGap",
        label: "80-95 Percentile Gap vs Max Ideal",
        color: COLORS.successGreen,
      },
      {
        key: "targetBaseline",
        label: `Max Ideal Target (${targetLeafSize.toFixed(1)} cm)`,
        color: "#8c8c8c",
      },
    ],
    growthRate: [
      {
        key: "avgGrowthRate",
        label: "Average Growth Rate",
        color: COLORS.primaryBlue,
      },
      {
        key: "percentileGrowthRate",
        label: "80-95 Percentile Growth Rate",
        color: COLORS.successGreen,
      },
      {
        key: "growthBaseline",
        label: "No-Change Baseline",
        color: "#8c8c8c",
      },
    ],
    stability: [
      {
        key: "stdDev",
        label: "Leaf Size Variability (Std Dev)",
        color: COLORS.primaryOrange,
      },
      {
        key: "coefficientVariation",
        label: "Consistency Score (CV %)",
        color: "#722ed1",
      },
    ],
    readiness: [
      {
        key: "avgReadiness",
        label: "Average Readiness (%)",
        color: COLORS.primaryBlue,
      },
      {
        key: "percentileReadiness",
        label: "80-95 Percentile Readiness (%)",
        color: COLORS.successGreen,
      },
      {
        key: "readinessBaseline",
        label: `Max Ideal Baseline (100% | ${targetLeafSize.toFixed(1)} cm)`,
        color: "#8c8c8c",
      },
      ...(minIdealReadinessPercent === null
        ? []
        : [
            {
              key: "minReadinessBaseline",
              label: `Min Ideal Baseline (${minIdealReadinessPercent.toFixed(
                1,
              )}% | ${minIdealLeafSize.toFixed(1)} cm)`,
              color: "#bfbfbf",
            },
          ]),
    ],
    cumulativeGrowth: [
      {
        key: "avgCumulativeGrowth",
        label: "Average Cumulative Growth",
        color: COLORS.primaryBlue,
      },
      {
        key: "percentileCumulativeGrowth",
        label: "80-95 Percentile Cumulative Growth",
        color: COLORS.successGreen,
      },
      {
        key: "cumulativeBaseline",
        label: "Start Baseline",
        color: "#8c8c8c",
      },
    ],
    upperTailLift: [
      {
        key: "upperTailLift",
        label: "Upper-Tail Lift (cm)",
        color: COLORS.primaryOrange,
      },
      {
        key: "upperTailLiftPercent",
        label: "Upper-Tail Lift (%)",
        color: "#722ed1",
      },
      {
        key: "upperTailBaseline",
        label: "No-Lift Baseline",
        color: "#8c8c8c",
      },
    ],
  };

  const currentAdvancedLegend = advancedLegendByGraphType[graphType] || [];

  const activeChartData =
    graphType === "targetGap"
      ? targetGapChartData
      : graphType === "growthRate"
      ? growthRateChartData
      : graphType === "stability"
      ? stabilityChartData
      : graphType === "readiness"
      ? readinessChartData
      : graphType === "cumulativeGrowth"
      ? cumulativeGrowthChartData
      : graphType === "upperTailLift"
      ? upperTailLiftChartData
      : averageChartData;

  const activeChartOptions =
    graphType === "targetGap"
      ? targetGapOptions
      : graphType === "growthRate"
      ? growthRateOptions
      : graphType === "stability"
      ? stabilityOptions
      : graphType === "readiness"
      ? readinessOptions
      : graphType === "cumulativeGrowth"
      ? cumulativeGrowthOptions
      : graphType === "upperTailLift"
      ? upperTailLiftOptions
      : averageOptions;

  const toggleLineVisibility = (label) => {
    setVisibility((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const toggleAdvancedVisibility = (key) => {
    setAdvancedVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isDefaultLineType = graphType === "line";

  return (
    <div className={`flex w-full flex-col ${isFullscreen ? "h-full min-h-0" : ""}`}>
      <h2 className={`font-bold ${isFullscreen ? "text-xl" : "text-2xl"}`}>
        Leaf Size (Average) Trend
      </h2>
      <div
        className={`graph-header flex w-full justify-between items-center sm:flex-col ${
          isFullscreen ? "mb-2 gap-2" : "mb-4"
        }`}
      >
        {isDefaultLineType ? (
          <div className="custom-legend flex gap-3 sm:flex-col">
            <div
              key="avg"
              className="flex items-center mb-1 cursor-pointer"
              onClick={() =>
                toggleLineVisibility("Average Spinach Leaf Size (cm)")
              }
            >
              <div
                className="relative w-10 h-0.5 mr-2"
                style={{
                  backgroundColor: COLORS.primaryBlue,
                  opacity: visibility["Average Spinach Leaf Size (cm)"] ? 1 : 0.3,
                }}
              >
                <div
                  style={{
                    backgroundColor: COLORS.primaryBlue,
                    opacity: visibility["Average Spinach Leaf Size (cm)"]
                      ? 1
                      : 0.3,
                  }}
                  className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                ></div>
              </div>
              <span
                style={{
                  opacity: visibility["Average Spinach Leaf Size (cm)"] ? 1 : 0.3,
                }}
              >
                Average
              </span>
            </div>

            <div
              key="percentile_80_95"
              className="flex items-center mb-1 cursor-pointer"
              onClick={() =>
                toggleLineVisibility("Leaf Size (80-95 percentile) Trend")
              }
            >
              <div
                className="relative w-10 h-0.5 mr-2"
                style={{
                  backgroundColor: COLORS.successGreen,
                  opacity: visibility["Leaf Size (80-95 percentile) Trend"]
                    ? 1
                    : 0.3,
                }}
              >
                <div
                  style={{
                    backgroundColor: COLORS.successGreen,
                    opacity: visibility["Leaf Size (80-95 percentile) Trend"]
                      ? 1
                      : 0.3,
                  }}
                  className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                ></div>
              </div>
              <span
                style={{
                  opacity: visibility["Leaf Size (80-95 percentile) Trend"]
                    ? 1
                    : 0.3,
                }}
              >
                Leaf Size (80-95 percentile) Trend
              </span>
            </div>

            <div
              key="standard_deviation"
              className="flex items-center mb-1 cursor-pointer"
              onClick={() => toggleLineVisibility("Standard Deviation")}
            >
              <div
                className="relative w-10 h-0.5 mr-2"
                style={{
                  backgroundColor: "#a9cff5",
                  opacity: visibility["Standard Deviation"] ? 1 : 0.3,
                }}
              >
                <div
                  style={{
                    backgroundColor: "#a9cff5",
                    opacity: visibility["Standard Deviation"] ? 1 : 0.3,
                  }}
                  className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                ></div>
              </div>
              <span
                style={{
                  opacity: visibility["Standard Deviation"] ? 1 : 0.3,
                }}
              >
                Leaf Size Range
              </span>
            </div>

            {forecastDataToUse.length > 0 && isForecastAboveIdeal && (
              <div
                key="forecast"
                className="flex items-center mb-1 cursor-pointer"
                onClick={() => toggleLineVisibility("Forecast Leaf Size")}
              >
                <div
                  className="relative w-10 h-0.5 mr-2"
                  style={{
                    backgroundColor: COLORS.primaryOrange,
                    opacity: visibility["Forecast Leaf Size"] ? 1 : 0.3,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: COLORS.primaryOrange,
                      opacity: visibility["Forecast Leaf Size"] ? 1 : 0.3,
                    }}
                    className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                  ></div>
                </div>
                <span
                  style={{
                    opacity: visibility["Forecast Leaf Size"] ? 1 : 0.3,
                  }}
                >
                  Forecast Leaf Size
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="custom-legend flex flex-wrap gap-3 sm:flex-col">
            {currentAdvancedLegend.map((item) => (
              <div
                key={item.key}
                className="flex items-center mb-1 cursor-pointer"
                onClick={() => toggleAdvancedVisibility(item.key)}
              >
                <div
                  className="relative w-10 h-0.5 mr-2"
                  style={{
                    backgroundColor: item.color,
                    opacity: advancedVisibility[item.key] ? 1 : 0.3,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: item.color,
                      opacity: advancedVisibility[item.key] ? 1 : 0.3,
                    }}
                    className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                  ></div>
                </div>
                <span
                  style={{
                    opacity: advancedVisibility[item.key] ? 1 : 0.3,
                  }}
                >
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        )}

        <div
          className={`flex items-center gap-3 ${
            isFullscreen
              ? "w-full flex-wrap justify-end sm:w-auto sm:justify-start"
              : "ml-auto flex-wrap justify-end"
          }`}
        >
          {isSuperUser && (
            <>
              <span
                className="text-xs font-semibold"
                style={{ color: COLORS.darkerGray }}
              >
                Graph Type:
              </span>
              <Select
                value={graphType}
                onChange={setGraphType}
                options={graphTypeOptions}
                className="w-[210px]"
              />
            </>
          )}
          <div className="flex items-center">
            <span style={{ marginRight: "8px" }}>
              All {blockLabel} Combined Graph
            </span>
            <Switch checked={isAllRowCombined} onChange={setIsAllRowCombined} />
          </div>
        </div>
      </div>

      {isSuperUser && (
        <Alert
          type="info"
          showIcon={false}
          className={`${isFullscreen ? "mb-2" : "mb-3"} !py-1`}
          style={{ border: "none", backgroundColor: "transparent" }}
          message={
            <div className="text-xs leading-5" style={{ color: COLORS.primaryBlue }}>
              <span className="font-semibold">{activeGraphInfo.short}</span>{" "}
              <span>{activeGraphInfo.relevance}</span>
              <Popover
                trigger="click"
                placement="bottomRight"
                title="How this graph is calculated"
                content={
                  <div className="max-w-[380px] text-xs leading-5">
                    <div className="mb-1 font-semibold">
                      {activeGraphInfo.relevance}
                    </div>
                    {activeGraphInfo.details.map((detail) => (
                      <div key={detail}>{detail}</div>
                    ))}
                  </div>
                }
              >
                <Button
                  type="text"
                  size="small"
                  aria-label="Graph calculation details"
                  icon={<InfoCircleOutlined style={{ color: COLORS.primaryBlue }} />}
                  className="!ml-1 !h-auto !w-auto !min-w-0 !p-0 align-middle"
                />
              </Popover>
            </div>
          }
        />
      )}

      <div
        className={`w-full ${
          isFullscreen
            ? "min-h-0 flex-1 overflow-x-auto overflow-y-hidden"
            : "overflow-x-auto"
        }`}
      >
        <div className={isFullscreen ? "h-full min-w-[760px]" : "min-w-[640px]"}>
          <Line data={activeChartData} options={activeChartOptions} />
        </div>
      </div>
    </div>
  );
};

export { AverageGraph };
